import { useState, useEffect, useRef } from 'react';
import './Requirements.css';

/* ════════════════════════════════════════════════════════════════════════
   Shared requirement picker modals.

   These are the SAME searchable modals the requirements table uses in edit
   mode, extracted here so the create + duplicate forms reuse them instead of
   native <select>s (catalogs like clients/users grow large over time).

   ⚠️ Each component returns a plain `.req-picker-backdrop` div — it does NOT
   portal itself. Callers MUST wrap the modal group in
   `createPortal(<…/>, document.body)`, because the requirements page's
   `.req-main` carries a permanent transform (entrance animation) that would
   otherwise become the containing block for the modal's `position:fixed`
   backdrop and mis-position it.
   ════════════════════════════════════════════════════════════════════════ */

/* Frontend sanity cap for requirements.comment (the DB column is unbounded
   text). Shared by the table's comment edit modal and both creation forms. */
export const COMMENT_MAXLEN = 2000;

/* ── Contrast helper for status badges ── */
export function getContrastColor(hex) {
  if (!hex) return '#222222';
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#222222' : '#FFFFFF';
}

/* ── Icons (self-contained) ── */
function IconClose() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/* ── Filter Picker Modal (searchable single-select) ───────────────────────
   `emptyOption` renders a clear/"all" row at the top. Defaults to
   { label: 'Todos' } for the table filter bar; pass `null` to hide it for a
   required form field. */
export function FilterPickerModal({ open, title, options, value, onSelect, onClose, labelKey, emptyOption = { label: 'Todos' } }) {
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const filtered = search.trim()
    ? options.filter((o) => {
        const label = typeof labelKey === 'function' ? labelKey(o) : o[labelKey || 'name'];
        return label.toLowerCase().includes(search.toLowerCase());
      })
    : options;

  return (
    <div className="req-picker-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="req-picker-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">{title}</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar">
            <IconClose />
          </button>
        </div>
        <div className="req-picker-search-wrap">
          <IconSearch />
          <input
            ref={inputRef}
            type="text"
            className="req-picker-search"
            placeholder="Buscar…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ul className="req-picker-list">
          {emptyOption && (
            <li>
              <button
                className={`req-picker-item${!value ? ' req-picker-item--active' : ''}`}
                onClick={() => { onSelect(''); onClose(); }}
              >
                <span className="req-picker-item-label">{emptyOption.label}</span>
              </button>
            </li>
          )}
          {filtered.map((o) => {
            const label = typeof labelKey === 'function' ? labelKey(o) : o[labelKey || 'name'];
            const isActive = String(o.id) === String(value);
            return (
              <li key={o.id}>
                <button
                  className={`req-picker-item${isActive ? ' req-picker-item--active' : ''}`}
                  onClick={() => { onSelect(o.id); onClose(); }}
                >
                  <span className="req-picker-item-label">{label}</span>
                  {isActive && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="req-picker-empty">Sin resultados</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* ── Specialization Picker Modal ───────────────────────────────────────── */
/* Generic single-select catalog picker ({ id, name } options + "none" entry).
   Also reused for Tipo de Facturación via `title` / `emptyLabel`. */
export function SpecializationPickerModal({ open, options, value, onSelect, onClose, title = 'Seleccionar Especialidad', emptyLabel = 'Sin especialidad' }) {
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const filtered = search.trim()
    ? options.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="req-picker-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="req-status-picker-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">{title}</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
        </div>
        <div className="req-picker-search-wrap">
          <IconSearch />
          <input
            ref={inputRef}
            type="text"
            className="req-picker-search"
            placeholder="Buscar…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ul className="req-picker-list">
          <li>
            <button
              className={`req-status-picker-item${!value ? ' req-status-picker-item--active' : ''}`}
              onClick={() => { onSelect(null); }}
            >
              <span className="req-picker-item-label" style={{ color: 'rgba(34,34,34,0.5)' }}>{emptyLabel}</span>
            </button>
          </li>
          {filtered.map((s) => {
            const isActive = String(s.id) === String(value);
            return (
              <li key={s.id}>
                <button
                  className={`req-status-picker-item${isActive ? ' req-status-picker-item--active' : ''}`}
                  onClick={() => { onSelect(s.id); }}
                >
                  <span className="req-picker-item-label">{s.name}</span>
                  {isActive && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00CFC1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                  )}
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="req-picker-empty">Sin resultados</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* ── Estimator / User Picker Modal (single-select) ─────────────────────── */
export function EstimatorPickerModal({ open, options, value, onSelect, onClose, title = 'Seleccionar Estimador', emptyLabel = 'Sin estimador', hideEmptyOption = false }) {
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const filtered = search.trim()
    ? options.filter((u) => u.full_name.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="req-picker-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="req-status-picker-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">{title}</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
        </div>
        <div className="req-picker-search-wrap">
          <IconSearch />
          <input
            ref={inputRef}
            type="text"
            className="req-picker-search"
            placeholder="Buscar por nombre…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ul className="req-picker-list">
          {!hideEmptyOption && (
            <li>
              <button
                className={`req-status-picker-item${!value ? ' req-status-picker-item--active' : ''}`}
                onClick={() => { onSelect(null); onClose(); }}
              >
                <span className="req-picker-item-label" style={{ color: 'rgba(34,34,34,0.5)' }}>{emptyLabel}</span>
                {!value && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00CFC1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </button>
            </li>
          )}
          {filtered.map((u) => {
            const isActive = String(u.id) === String(value);
            return (
              <li key={u.id}>
                <button
                  className={`req-status-picker-item${isActive ? ' req-status-picker-item--active' : ''}`}
                  onClick={() => { onSelect(u.id); onClose(); }}
                >
                  <span className="req-picker-item-label">{u.full_name}</span>
                  {isActive && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00CFC1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                  )}
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="req-picker-empty">Sin resultados</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* ── Status Picker Modal (colored badges) ──────────────────────────────── */
export function StatusPickerModal({ open, options, value, onSelect, onClose, emptyLabel = 'Sin estatus' }) {
  const backdropRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="req-picker-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="req-status-picker-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">Seleccionar Estatus</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
        </div>
        <ul className="req-picker-list">
          <li>
            <button
              className={`req-status-picker-item${!value ? ' req-status-picker-item--active' : ''}`}
              onClick={() => { onSelect(null); }}
            >
              <span className="req-picker-item-label" style={{ color: 'rgba(34,34,34,0.5)' }}>{emptyLabel}</span>
            </button>
          </li>
          {options.map((s) => {
            const isActive = String(s.id) === String(value);
            return (
              <li key={s.id}>
                <button
                  className={`req-status-picker-item${isActive ? ' req-status-picker-item--active' : ''}`}
                  onClick={() => { onSelect(s.id); }}
                >
                  <span className="req-status-badge" style={{ backgroundColor: s.color, color: getContrastColor(s.color) }}>
                    {s.name}
                  </span>
                  {isActive && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00CFC1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/* ── Status Filter Modal (multi-select, colored badges) ────────────────────
   Used by the requirements filter bar to filter by ONE OR MORE statuses.
   Mirrors DevPickerModal's local-selection + confirm/cancel pattern, but each
   row shows the status' colored badge so the user sees which color is which
   estatus. `selectedIds` is an array of status id strings. */
export function StatusFilterModal({ open, options, selectedIds, onConfirm, onClose }) {
  const [localSelection, setLocalSelection] = useState(() => new Set((selectedIds || []).map(String)));
  const backdropRef = useRef(null);

  useEffect(() => {
    if (open) {
      setLocalSelection(new Set((selectedIds || []).map(String)));
    }
  }, [open, selectedIds]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  function toggleStatus(statusId) {
    setLocalSelection((prev) => {
      const next = new Set(prev);
      const key = String(statusId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleConfirm() {
    onConfirm([...localSelection]);
    onClose();
  }

  const allSelected = options.length > 0 && localSelection.size === options.length;

  function toggleAll() {
    setLocalSelection(allSelected ? new Set() : new Set(options.map((s) => String(s.id))));
  }

  return (
    <div className="req-picker-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="req-dev-picker-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">Filtrar por Estatus</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
        </div>
        <ul className="req-picker-list req-dev-picker-list">
          <li>
            <button className="req-dev-picker-item" onClick={toggleAll}>
              <div className={`req-dev-picker-check${allSelected ? ' req-dev-picker-check--on' : ''}`}>
                {allSelected && (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </div>
              <span className="req-dev-picker-name" style={{ fontWeight: 600, color: 'rgba(34,34,34,0.6)' }}>
                {allSelected ? 'Quitar todos' : 'Seleccionar todos'}
              </span>
            </button>
          </li>
          {options.map((s) => {
            const isSelected = localSelection.has(String(s.id));
            return (
              <li key={s.id}>
                <button
                  className={`req-dev-picker-item${isSelected ? ' req-dev-picker-item--selected' : ''}`}
                  onClick={() => toggleStatus(s.id)}
                >
                  <div className={`req-dev-picker-check${isSelected ? ' req-dev-picker-check--on' : ''}`}>
                    {isSelected && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                    )}
                  </div>
                  <span className="req-dev-picker-name">
                    <span className="req-status-badge" style={{ backgroundColor: s.color, color: getContrastColor(s.color) }}>
                      {s.name}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
          {options.length === 0 && (
            <li className="req-picker-empty">Sin estatus disponibles</li>
          )}
        </ul>
        <div className="req-dev-picker-footer">
          <span className="req-dev-picker-count">
            {localSelection.size === 0
              ? 'Todos los estatus'
              : `${localSelection.size} seleccionado${localSelection.size !== 1 ? 's' : ''}`}
          </span>
          <div className="req-dev-picker-actions">
            <button className="req-dev-picker-cancel" onClick={onClose}>Cancelar</button>
            <button className="req-dev-picker-confirm" onClick={handleConfirm}>Aplicar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Billed Filter Modal (tri-state: Todos / No facturado / Facturado) ─────
   Mirrors the "Facturado" column's checkmark visual so the user sees which
   option maps to which state. `value` is '' (Todos) | 'false' | 'true'. */
export function BilledFilterModal({ open, value, onSelect, onClose }) {
  const backdropRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const options = [
    { key: '', label: 'Todos', billed: null },
    { key: 'false', label: 'No facturado', billed: false },
    { key: 'true', label: 'Facturado', billed: true },
  ];

  function pick(key) {
    onSelect(key);
    onClose();
  }

  return (
    <div className="req-picker-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="req-status-picker-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">Filtrar por Facturado</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
        </div>
        <ul className="req-picker-list">
          {options.map((o) => {
            const isActive = String(value) === o.key;
            return (
              <li key={o.key || 'all'}>
                <button
                  className={`req-status-picker-item${isActive ? ' req-status-picker-item--active' : ''}`}
                  onClick={() => pick(o.key)}
                >
                  {o.billed === null ? (
                    <span className="req-picker-item-label" style={{ color: 'rgba(34,34,34,0.5)' }}>{o.label}</span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                      <span className={`req-billed-check${o.billed ? ' req-billed-check--on' : ''}`}>
                        {o.billed && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                        )}
                      </span>
                      <span className="req-picker-item-label">{o.label}</span>
                    </span>
                  )}
                  {isActive && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00CFC1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/* ── Date Picker Modal (calendar) ──────────────────────────────────────── */
export function DatePickerModal({ open, value, onSelect, onClose }) {
  const [viewYear, setViewYear] = useState(() => value ? new Date(value + 'T12:00:00').getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? new Date(value + 'T12:00:00').getMonth() : new Date().getMonth());
  const backdropRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (value) {
        const d = new Date(value + 'T12:00:00');
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      } else {
        const now = new Date();
        setViewYear(now.getFullYear());
        setViewMonth(now.getMonth());
      }
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const dayLabels = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

  const firstDay = new Date(viewYear, viewMonth, 1);
  const lastDay = new Date(viewYear, viewMonth + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  }

  function selectDay(day) {
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onSelect(dateStr);
  }

  return (
    <div className="req-picker-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="req-date-picker-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">Seleccionar Fecha</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
        </div>
        <div className="req-cal-nav">
          <button className="req-cal-nav-btn" onClick={prevMonth} aria-label="Mes anterior"><IconChevronLeft /></button>
          <span className="req-cal-month">{monthNames[viewMonth]} {viewYear}</span>
          <button className="req-cal-nav-btn" onClick={nextMonth} aria-label="Mes siguiente"><IconChevronRight /></button>
        </div>
        <div className="req-cal-grid">
          {dayLabels.map((d) => <div key={d} className="req-cal-dayname">{d}</div>)}
          {cells.map((day, i) => {
            if (day === null) return <div key={`empty-${i}`} className="req-cal-cell req-cal-cell--empty" />;
            const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = dateStr === value;
            const isToday = dateStr === todayStr;
            return (
              <button
                key={day}
                className={`req-cal-cell${isSelected ? ' req-cal-cell--selected' : ''}${isToday && !isSelected ? ' req-cal-cell--today' : ''}`}
                onClick={() => selectDay(day)}
              >
                {day}
              </button>
            );
          })}
        </div>
        <div className="req-cal-actions">
          <button className="req-cal-clear" onClick={() => { onSelect(null); }}>Limpiar fecha</button>
        </div>
      </div>
    </div>
  );
}

/* ── Dev Picker Modal (multi-select) ───────────────────────────────────── */
export function DevPickerModal({ open, options, selectedIds, onConfirm, onClose, title = 'Asignar Desarrolladores', confirmLabel = 'Confirmar', emptyCountLabel = 'Sin selección' }) {
  const [localSelection, setLocalSelection] = useState(() => new Set((selectedIds || []).map(String)));
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (open) {
      setLocalSelection(new Set((selectedIds || []).map(String)));
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, selectedIds]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const filtered = search.trim()
    ? options.filter((u) => u.full_name.toLowerCase().includes(search.toLowerCase()))
    : options;

  function toggleUser(userId) {
    setLocalSelection((prev) => {
      const next = new Set(prev);
      const key = String(userId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleConfirm() {
    onConfirm([...localSelection]);
    onClose();
  }

  return (
    <div className="req-picker-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="req-dev-picker-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">{title}</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
        </div>
        <div className="req-picker-search-wrap">
          <IconSearch />
          <input
            ref={inputRef}
            type="text"
            className="req-picker-search"
            placeholder="Buscar por nombre…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ul className="req-picker-list req-dev-picker-list">
          {filtered.map((u) => {
            const isSelected = localSelection.has(String(u.id));
            const isLider = u.roles?.name === 'Líder';
            return (
              <li key={u.id}>
                <button
                  className={`req-dev-picker-item${isSelected ? ' req-dev-picker-item--selected' : ''}`}
                  onClick={() => toggleUser(u.id)}
                >
                  <div className={`req-dev-picker-check${isSelected ? ' req-dev-picker-check--on' : ''}`}>
                    {isSelected && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                    )}
                  </div>
                  <span className="req-dev-picker-name">{u.full_name}</span>
                  <span className={`req-dev-picker-role${isLider ? ' req-dev-picker-role--lider' : ' req-dev-picker-role--dev'}`}>
                    {isLider ? 'Líder' : 'Desarrollador'}
                  </span>
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="req-picker-empty">Sin resultados</li>
          )}
        </ul>
        <div className="req-dev-picker-footer">
          <span className="req-dev-picker-count">
            {localSelection.size === 0
              ? emptyCountLabel
              : `${localSelection.size} seleccionado${localSelection.size !== 1 ? 's' : ''}`}
          </span>
          <div className="req-dev-picker-actions">
            <button className="req-dev-picker-cancel" onClick={onClose}>Cancelar</button>
            <button className="req-dev-picker-confirm" onClick={handleConfirm}>{confirmLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Root Picker Modal (single-select requirement) ─────────────────────── */
export function RootPickerModal({ open, options, currentId, onConfirm, onClose }) {
  const [selected, setSelected] = useState(currentId ?? null);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSelected(currentId ?? null);
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, currentId]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? options.filter((r) =>
        r.reference_id.toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q)
      )
    : options;

  return (
    <div className="req-picker-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="req-root-picker-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">Seleccionar Folio Raíz</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
        </div>
        <div className="req-picker-search-wrap">
          <IconSearch />
          <input
            ref={inputRef}
            type="text"
            className="req-picker-search"
            placeholder="Buscar por folio o descripción…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ul className="req-picker-list req-root-picker-list">
          <li className="req-root-picker-none">
            <button
              className={`req-root-picker-item${selected === null ? ' req-root-picker-item--selected' : ''}`}
              onClick={() => setSelected(null)}
            >
              <div className={`req-root-picker-dot${selected === null ? ' req-root-picker-dot--on' : ''}`} />
              <div className="req-root-picker-text">
                <span className="req-root-picker-folio">Sin raíz (requerimiento original)</span>
                <span className="req-root-picker-desc">Desvincula este folio de cualquier raíz</span>
              </div>
            </button>
          </li>
          {filtered.map((r) => {
            const isSel = selected === r.id;
            return (
              <li key={r.id}>
                <button
                  className={`req-root-picker-item${isSel ? ' req-root-picker-item--selected' : ''}`}
                  onClick={() => setSelected(r.id)}
                >
                  <div className={`req-root-picker-dot${isSel ? ' req-root-picker-dot--on' : ''}`} />
                  <div className="req-root-picker-text">
                    <span className="req-root-picker-folio">{r.reference_id}</span>
                    <span className="req-root-picker-desc">{r.description || '—'}</span>
                  </div>
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="req-picker-empty">Sin resultados</li>
          )}
        </ul>
        <div className="req-dev-picker-footer">
          <span className="req-dev-picker-count">
            {selected === null ? 'Sin raíz seleccionada' : 'Raíz seleccionada'}
          </span>
          <div className="req-dev-picker-actions">
            <button className="req-dev-picker-cancel" onClick={onClose}>Cancelar</button>
            <button className="req-dev-picker-confirm" onClick={() => { onConfirm(selected); onClose(); }}>Confirmar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
